import type { ReactNode } from 'react';
import { Fragment } from 'react';

/**
 * A deliberately small markdown renderer: paragraphs, headings, bullet and
 * numbered lists, simple pipe tables, and inline bold / italic / code.
 *
 * Agent text streams in token by token, so this has to cope with half-written
 * syntax on every render — it never throws, and anything it doesn't recognise
 * falls through as plain text.
 */

const INLINE = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|`[^`\n]+`)/g;

function inline(text: string, keyPrefix: string): ReactNode {
  const parts = text.split(INLINE).filter((part) => part !== '');
  if (parts.length === 1) return parts[0] ?? '';
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if ((part.startsWith('**') && part.endsWith('**') && part.length > 4) ||
        (part.startsWith('__') && part.endsWith('__') && part.length > 4)) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={key}>{part.slice(1, -1)}</code>;
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

function splitRow(line: string): string[] {
  return line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

const isTableRow = (line: string): boolean => /^\s*\|/.test(line);
const isTableDivider = (line: string): boolean => /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(line) && line.includes('-');
const bulletMatch = (line: string): RegExpMatchArray | null => line.match(/^\s*[-*+]\s+(.*)$/);
const orderedMatch = (line: string): RegExpMatchArray | null => line.match(/^\s*\d+[.)]\s+(.*)$/);

export function renderMarkdown(source: string): ReactNode {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let paragraph: string[] = [];
  let key = 0;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(' ').trim();
    paragraph = [];
    if (text) out.push(<p key={`p${key++}`}>{inline(text, `p${key}`)}</p>);
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? '';

    if (!line.trim()) {
      flushParagraph();
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushParagraph();
      const level = (heading[1] ?? '#').length;
      const text = heading[2] ?? '';
      const Tag = (level <= 2 ? 'h3' : 'h4') as 'h3' | 'h4';
      out.push(<Tag key={`h${key++}`}>{inline(text, `h${key}`)}</Tag>);
      continue;
    }

    // Table: a header row, an optional divider, then body rows.
    if (isTableRow(line) && isTableRow(lines[i + 1] ?? '') && isTableDivider(lines[i + 1] ?? '')) {
      flushParagraph();
      const header = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i] ?? '')) {
        rows.push(splitRow(lines[i] ?? ''));
        i += 1;
      }
      i -= 1;
      out.push(
        <div className="md-table-wrap" key={`t${key++}`}>
          <table className="md-table">
            <thead>
              <tr>
                {header.map((cell, c) => (
                  <th key={c}>{inline(cell, `th${c}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{inline(cell, `td${r}-${c}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (bulletMatch(line)) {
      flushParagraph();
      const items: string[] = [];
      while (i < lines.length) {
        const match = bulletMatch(lines[i] ?? '');
        if (!match) break;
        items.push(match[1] ?? '');
        i += 1;
      }
      i -= 1;
      out.push(
        <ul key={`ul${key++}`}>
          {items.map((item, n) => (
            <li key={n}>{inline(item, `li${n}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (orderedMatch(line)) {
      flushParagraph();
      const items: string[] = [];
      while (i < lines.length) {
        const match = orderedMatch(lines[i] ?? '');
        if (!match) break;
        items.push(match[1] ?? '');
        i += 1;
      }
      i -= 1;
      out.push(
        <ol key={`ol${key++}`}>
          {items.map((item, n) => (
            <li key={n}>{inline(item, `oli${n}`)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    paragraph.push(line.trim());
  }

  flushParagraph();
  return out;
}

/** Renders markdown into a plain wrapper. */
export function Markdown({ text }: { text: string }): JSX.Element {
  return <div className="md">{renderMarkdown(text)}</div>;
}
